import {LabelIdentifiers, PlcCodes} from '@dch/products-business-logic';

import jwtDecode from 'jwt-decode';

export function getOverviewProductType(plc, labelIdentifier): string {
  switch (plc) {
    case PlcCodes.FI:
      return labelIdentifier === LabelIdentifiers.FON ? 'FON' : 'INTERNET';
    case PlcCodes.MMAL:
      return 'MAIL';
    case PlcCodes.MV:
    case PlcCodes.MI:
    case PlcCodes.MMI:
    case PlcCodes.MMV:
      return labelIdentifier === LabelIdentifiers.FON ? 'FON' : 'MOBILE';
    case PlcCodes.TV:
      return 'TV';
    case PlcCodes.EP:
      return 'EPRESS';
    case PlcCodes.CLD:
      return 'CLOUD';
    case PlcCodes.SEC:
      return 'NORTON';
    case PlcCodes.FT:
      return 'TELEPHONY';
    case PlcCodes.INS:
      return 'CYBER-CARE';
  }
}

export function shouldRedirectToEshareUserJourney(productMpcuidList: string[], eshareMpcuidDisallowlist: string): boolean {
  if (!productMpcuidList?.length) {
    return true;
  }
  if (eshareMpcuidDisallowlist && eshareMpcuidDisallowlist.trim().length > 0) {
    const mpcuids = eshareMpcuidDisallowlist.split(',').map(id => id.trim());

    for (const mpcuid of mpcuids) {
      if (isMpcuidDisallowed(mpcuid, productMpcuidList)) {
        return false;
      }
    }
  }
  return true;
}

function isMpcuidDisallowed(mpcuid: string, productMpcuidList: string[]): boolean {
  const [included, excluded] = mpcuid.split('!').map(id => id.trim());
  if (!excluded) {
    return productMpcuidList.includes(included);
  }
  return productMpcuidList.includes(included) && !productMpcuidList.includes(excluded);
}

/**
 * Checks whether the given mpcuid is part of a comma-separated blacklist.
 * Used to skip the eShare/DA option-flow redirect and stay on the legacy page.
 * @param mpcuid - The current option/product mpcuid to test
 * @param blacklist - Comma-separated list of blacklisted mpcuids
 * @returns true when the mpcuid is blacklisted, false otherwise
 */
export function isMpcuidBlacklisted(mpcuid: string, blacklist: string): boolean {
  if (!mpcuid || !blacklist || blacklist.trim().length === 0) {
    return false;
  }
  return blacklist
    .split(',')
    .map(id => id.trim())
    .filter(id => !!id)
    .includes(mpcuid.trim());
}

export function getProductMpcuidList(product: any) {
  const productMpcuidList = [];
  if (product.mpcUuid) {
    productMpcuidList.push(product.mpcUuid);
  }

  if (product.groupMpcUuid) {
    productMpcuidList.push(product.groupMpcUuid);
  }

  if (product.products?.length) {
    product.products.forEach(item => {
      if (item.groupMpcUuid) {
        productMpcuidList.push(item.groupMpcUuid);
      }
    });
  }
  return [...new Set(productMpcuidList)];
}

export class JwtUtils {
  public static decodeToken(token: string): any {
    try {
      return jwtDecode(token);
    } catch (error) {
      console.error('Invalid token', error);
      return null;
    }
  }
}

export interface EshareQueryParams {
  intent?: string;
  lomid?: string;
  mpcuid?: string;
  action?: string;
}

/**
 * Constructs a URL with JWT token and optional query parameters
 * @param url - Base URL
 * @param token - JWT token
 * @param params - Optional query parameters (intent, lomid, mpcuid, action)
 * @returns Constructed URL with all parameters
 */
export function constructEshareUrl(url: string, token: string, params: EshareQueryParams = {}): string {
  let updatedUrl = `${url}?token=${token}&type=jwt`;

  if (params.intent) {
    updatedUrl += `&intent=${params.intent}`;
  }

  if (params.lomid) {
    updatedUrl += `&lomid=${params.lomid}`;
  }

  if (params.mpcuid) {
    updatedUrl += `&mpcuid=${params.mpcuid}`;
  }

  if (params.action) {
    updatedUrl += `&action=${params.action}`;
  }

  return updatedUrl;
}

